import React, { useEffect } from "react";
import { Button, Form, Modal, Spinner } from "react-bootstrap";
import toast from "react-hot-toast";
import {
  useCreateHeadquarter,
  useUpdateHeadquarter,
} from "../hooks/useHeadquarters";
import {
  useAcademyPolicy,
  useUpdateAcademyPolicy,
} from "../hooks/useAcademyPolicy";
import { useMemberships } from "../../billing/hooks/useMemberships";

const EMPTY = {
  neighborhood: "",
  address: "",
  reference_name: "",
  phone: "",
  administrator: "",
};

function HeadquartersForm({ show, onHide, editing }) {
  const [values, setValues] = React.useState(EMPTY);
  // Multisede: solo relevante al crear (es una política de toda la academia,
  // no de la sede puntual que se está editando) — se precarga desde la
  // política vigente y solo se reenvía si el usuario la cambió.
  const [sharing, setSharing] = React.useState(null);

  const createMutation = useCreateHeadquarter();
  const updateMutation = useUpdateHeadquarter();
  const mutation = editing ? updateMutation : createMutation;
  const { data: policy } = useAcademyPolicy();
  const updatePolicyMutation = useUpdateAcademyPolicy();
  // Solo hace falta elegir administrador cuando se va a crear una sede y la
  // academia no comparte algo entre sedes — se pide a los propietarios
  // activos (el administrador debe ser OWNER, lo valida también el backend).
  const sharingOff =
    !editing &&
    sharing &&
    (!sharing.shares_financial_matters || !sharing.shares_operational_matters);
  const { data: owners } = useMemberships(sharingOff ? "OWNER" : undefined);

  useEffect(() => {
    setValues(editing ? { ...editing } : EMPTY);
  }, [editing, show]);

  useEffect(() => {
    if (!editing && policy) {
      setSharing({
        shares_financial_matters: policy.shares_financial_matters,
        shares_operational_matters: policy.shares_operational_matters,
      });
    }
  }, [editing, policy, show]);

  const set = (field) => (e) =>
    setValues((v) => ({ ...v, [field]: e.target.value }));

  const setSharingField = (field) => (e) =>
    setSharing((s) => ({ ...s, [field]: e.target.checked }));

  const handleSubmit = (e) => {
    e.preventDefault();
    let payload = editing ? { id: editing.id, ...values } : values;
    if (!sharingOff) {
      const { administrator, ...rest } = payload;
      payload = rest;
    }
    mutation.mutate(payload, {
      onSuccess: () => {
        const sharingChanged =
          !editing &&
          sharing &&
          policy &&
          (sharing.shares_financial_matters !== policy.shares_financial_matters ||
            sharing.shares_operational_matters !== policy.shares_operational_matters);
        if (sharingChanged) {
          updatePolicyMutation.mutate(sharing);
        }
        toast.success(editing ? "Sede actualizada" : "Sede creada");
        onHide();
      },
      onError: () => toast.error("Error al guardar la sede"),
    });
  };

  return (
    <Modal show={show} onHide={onHide} centered>
      <Modal.Header closeButton>
        <Modal.Title>{editing ? "Editar sede" : "Nueva sede"}</Modal.Title>
      </Modal.Header>
      <Form onSubmit={handleSubmit}>
        <Modal.Body>
          <Form.Group className="mb-3" controlId="hq-neighborhood">
            <Form.Label>Barrio</Form.Label>
            <Form.Control
              value={values.neighborhood}
              onChange={set("neighborhood")}
              placeholder="Barrio"
              required
              style={{ minHeight: 44 }}
            />
          </Form.Group>
          <Form.Group className="mb-3" controlId="hq-address">
            <Form.Label>Dirección</Form.Label>
            <Form.Control
              value={values.address}
              onChange={set("address")}
              placeholder="Dirección completa"
              required
              style={{ minHeight: 44 }}
            />
          </Form.Group>
          <Form.Group className="mb-3" controlId="hq-reference">
            <Form.Label>Nombre de referencia</Form.Label>
            <Form.Control
              value={values.reference_name}
              onChange={set("reference_name")}
              placeholder="Ej. Sede Centro"
              style={{ minHeight: 44 }}
            />
          </Form.Group>
          <Form.Group className="mb-3" controlId="hq-phone">
            <Form.Label>Teléfono</Form.Label>
            <Form.Control
              type="tel"
              value={values.phone}
              onChange={set("phone")}
              placeholder="Teléfono de contacto"
              style={{ minHeight: 44 }}
            />
          </Form.Group>

          {!editing && sharing && (
            <>
              <hr />
              <h6 className="text-uppercase text-muted small mb-3">
                Multisede
              </h6>
              <Form.Check
                type="switch"
                id="hq-shares-financial"
                label="La academia comparte temas económicos entre sedes"
                checked={Boolean(sharing.shares_financial_matters)}
                onChange={setSharingField("shares_financial_matters")}
                className="mb-3"
                style={{ minHeight: 44 }}
              />
              <Form.Text muted className="d-block mb-3">
                Si lo desactivas, cartera, tarifas y honorarios se podrán
                separar por sede más adelante.
              </Form.Text>
              <Form.Check
                type="switch"
                id="hq-shares-operational"
                label="La academia comparte temas operativos entre sedes"
                checked={Boolean(sharing.shares_operational_matters)}
                onChange={setSharingField("shares_operational_matters")}
                className="mb-3"
                style={{ minHeight: 44 }}
              />
              <Form.Text muted className="d-block mb-3">
                Si lo desactivas, agenda y estudiantes se podrán separar por
                sede más adelante.
              </Form.Text>

              {sharingOff && (
                <Form.Group className="mb-3" controlId="hq-administrator">
                  <Form.Label>Administrador de esta sede</Form.Label>
                  <Form.Select
                    value={values.administrator}
                    onChange={set("administrator")}
                    required
                    style={{ minHeight: 44 }}
                  >
                    <option value="">Selecciona un propietario…</option>
                    {(owners ?? []).map((m) => (
                      <option key={m.user.id} value={m.user.id}>
                        {m.user.full_name || m.user.email}
                      </option>
                    ))}
                  </Form.Select>
                  <Form.Text muted>
                    No se podrá cambiar después de crear la sede.
                  </Form.Text>
                </Form.Group>
              )}
            </>
          )}

          {editing && editing.administrator_display && (
            <Form.Text muted className="d-block">
              Administrador de esta sede:{" "}
              <strong>{editing.administrator_display}</strong> (no se puede
              cambiar).
            </Form.Text>
          )}
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={onHide} style={{ minHeight: 44 }}>
            Cancelar
          </Button>
          <Button
            type="submit"
            variant="primary"
            disabled={mutation.isPending}
            style={{ minHeight: 44 }}
          >
            {mutation.isPending ? (
              <Spinner animation="border" size="sm" />
            ) : editing ? (
              "Guardar cambios"
            ) : (
              "Crear sede"
            )}
          </Button>
        </Modal.Footer>
      </Form>
    </Modal>
  );
}

export { HeadquartersForm };
